/**
 * usePeople — ONE row per human, merged from the two rosters the product
 * already serves:
 *
 *   • tenant members  (`listTeamMembers` via shell/access) — who can sign in,
 *     their workspace role and their per-process role assignments.
 *   • staff records   (`listStaff` via screens/staff/useStaff) — who is
 *     bookable, on which channels, at which locations, with which services.
 *
 * They are matched on lowercased email. A member with no staff record is
 * login-only; a staff record with no member is bookable-with-no-sign-in. That
 * is the whole "a staff member is a user who can be assigned a role" model,
 * assembled client-side until identity ships one record (the merge is
 * DERIVED from two real reads — nothing here is seeded or invented).
 *
 * Tenants without staff / location / catalogue documents simply resolve to
 * empty arrays, and the screen renders its empty states. A FAILED read is
 * carried separately in `error` so it can never be painted as "nobody here".
 */
import { useEffect, useMemo, useState } from "react";
import {
  listMembers, hydrateMembers, subscribeMembers, getMembersError, effectivePerms,
  initialsOf, colorFor, PROCESSES, getRoleDef, type Member, type ProcessId,
} from "../../shell/access";
import { useStaff, type StaffMember } from "../staff/useStaff";
import { useLocations } from "../locations/useLocations";

export type PersonStatus = "active" | "invited" | "inactive" | "on-leave";

export interface PersonLocation { id: string; name: string }

/** Where someone works and the role they hold THERE (2026-08-08, Staff & Roles).
 *  Per-location and deliberately unranked — nothing computes an effective role
 *  from a set of these; see docs/staff-and-roles-spec.md. */
export interface PersonLocationRole { locationId: string; roleKey: string; home: boolean }

export interface Person {
  /** Stable identity for the row — email when we have one, else the record id. */
  key: string;
  memberId: string | null;
  staffId: string | null;
  name: string;
  email: string;
  title: string;
  init: string;
  color: string;
  photo?: string;
  /** Sign-in is enabled for this person (a membership, or a staff record whose
   *  own `signsIn` says so). */
  signsIn: boolean;
  /** A tenant MEMBERSHIP was served for them. `signsIn` without this is the
   *  third state — sign-in enabled on the staff record, no membership row —
   *  and it must not be reported as "no login". */
  membership: boolean;
  /** Appears in availability on at least one channel. */
  bookable: boolean;
  status: PersonStatus;
  /** Display label for the workspace/tenant role, as the team API reports it. */
  roleLabel: string;
  /** Tenant role KEY — the vocabulary member writes use. */
  roleKey: string;
  /** Per-process role assignments — what `can()` resolves against. */
  assignments: Partial<Record<ProcessId, string>>;
  /** The role every location inherits unless it sets its own (operator,
   *  2026-08-08). Lives on the roster record, so a person with no workspace
   *  membership has one too. "" when nothing resolvable is recorded. */
  defaultRoleKey: string;
  channels: string[];
  locations: PersonLocation[];
  /** The same assignments as `locations`, carrying the role held at each and
   *  which one is home — what `setStaffLocationRoles` reads and writes. Empty
   *  for a person with no roster record (their locations live on it). */
  locationRoles: PersonLocationRole[];
  groups: string[];
  capCount: number;
  last: string;
}

const lc = (s: string) => (s || "").trim().toLowerCase();

/* A roster record's `role` is meant to hold a Studio role, but the adapter
   falls back to the JOB TITLE when it is empty ("Home loans specialist"), so a
   raw read would hand the picker a value no catalogue knows. Only a name the
   role catalogue actually resolves is treated as a default role. */
function knownRole(k: string | null | undefined): string {
  if (!k) return "";
  return PROCESSES.some((p) => !!getRoleDef(k, p.id)) ? k : "";
}

/** Staff `locations` may be ids or `{ locationId, roleKey?, home?, schedules }`
 *  assignments. Normalised to one shape here so the screens never branch on it;
 *  when no row claims `home`, the first one is it. */
function staffLocationRoles(s: StaffMember | undefined): PersonLocationRole[] {
  const raw = (s?.locations ?? []) as unknown[];
  const rows = raw
    .map((l) => (typeof l === "string"
      ? { locationId: l, roleKey: "", home: false }
      : {
          locationId: (l as { locationId?: string })?.locationId ?? "",
          roleKey: (l as { roleKey?: string | null })?.roleKey ?? "",
          home: (l as { home?: boolean })?.home === true,
        }))
    .filter((r) => r.locationId);
  if (rows.length && !rows.some((r) => r.home)) rows[0].home = true;
  return rows;
}

function staffLocationIds(s: StaffMember | undefined): string[] {
  return staffLocationRoles(s).map((r) => r.locationId);
}

function personFromMember(m: Member, s: StaffMember | undefined, locName: (id: string) => string): Person {
  const assignments = (m.assignments && Object.keys(m.assignments).length
    ? m.assignments
    : (m.processRole ? { events: m.processRole } : {})) as Partial<Record<ProcessId, string>>;
  const channels = s?.channels ?? [];
  const status: PersonStatus = m.status === "invited"
    ? "invited"
    : ((s?.status as PersonStatus) ?? (m.status === "active" ? "active" : "active"));
  return {
    key: lc(m.email) || m.id,
    memberId: m.id,
    staffId: s?.id ?? null,
    name: m.name || s?.name || m.email,
    email: m.email || s?.email || "",
    title: s?.title || "",
    init: m.init || initialsOf(m.name || m.email),
    color: m.color || colorFor(m.email || m.id),
    photo: s?.photo || undefined,
    signsIn: true,
    membership: true,
    bookable: channels.length > 0,
    status,
    roleLabel: m.role,
    roleKey: m.roleKey,
    assignments,
    defaultRoleKey: knownRole(s?.role) || assignments.events || Object.values(assignments).find(Boolean) || "",
    channels,
    locations: staffLocationIds(s).map((id) => ({ id, name: locName(id) })),
    locationRoles: staffLocationRoles(s),
    groups: s?.groups ?? [],
    capCount: m.apiCaps
      ? m.apiCaps.length
      : effectivePerms({ role: m.processRole || m.role, assignments, grants: m.grants, revokes: m.revokes }).size,
    last: m.last || "—",
  };
}

function personFromStaff(s: StaffMember, locName: (id: string) => string): Person {
  const name = [s.firstName, s.lastName].filter(Boolean).join(" ") || s.name || s.email || s.id;
  return {
    key: lc(s.email) || s.id,
    memberId: null,
    staffId: s.id,
    name,
    email: s.email || "",
    title: s.title || "",
    init: initialsOf(name),
    color: s.color || colorFor(s.email || s.id),
    photo: s.photo || undefined,
    /* READ the record, don't assume. `signsIn` is a real field on
       CanonicalStaffMember — the Staff editor labels it "Can sign in? / Has a
       login" — and hardcoding false here labelled six people who explicitly
       have one as "No sign-in", in a drawer that was printing their work email
       40px above the claim. */
    signsIn: s.signsIn === true,
    membership: false,
    bookable: (s.channels ?? []).length > 0,
    status: (s.status as PersonStatus) ?? "active",
    roleLabel: "",
    roleKey: "",
    assignments: {},
    defaultRoleKey: knownRole(s.role),
    channels: s.channels ?? [],
    locations: staffLocationIds(s).map((id) => ({ id, name: locName(id) })),
    locationRoles: staffLocationRoles(s),
    groups: s.groups ?? [],
    capCount: 0,
    last: "—",
  };
}

export interface PeopleState {
  people: Person[];
  /** The underlying records, for the drawer's edit path. */
  memberOf: (p: Person) => Member | undefined;
  staffOf: (p: Person) => StaffMember | undefined;
  locations: { id: string; name: string }[];
  loading: boolean;
  /** Set ONLY when there is nothing honest to show — never for an empty roster,
   *  and never when the member read succeeded (see the degrade rule below). */
  error: string | null;
  /** The member read FAILED: rows may be staff-only, so sign-in and role are
   *  unknown for everyone — the screen must not assert "no sign-in". */
  membersUnavailable: boolean;
  /** The staff read failed while members loaded: rows are real, but the
   *  bookable half of every record is unknown. The screen says so inline
   *  instead of hiding nine people who loaded fine. */
  bookabilityUnavailable: boolean;
  /** The locations read failed — location cells and the filter are unknown. */
  locationsUnavailable: boolean;
  reload: () => void;
}

export function usePeople(): PeopleState {
  const [, force] = useState(0);
  const staff = useStaff();
  const locs = useLocations();

  useEffect(() => {
    hydrateMembers();
    const unsub = subscribeMembers(() => force((n) => n + 1));
    return () => { if (typeof unsub === "function") unsub(); };
  }, []);

  const members = listMembers();
  const membersError = getMembersError();
  const locName = useMemo(() => {
    const map = new Map(locs.locations.map((l) => [l.id, l.name]));
    return (id: string) => map.get(id) ?? id;
  }, [locs.locations]);

  const people = useMemo(() => {
    const byEmail = new Map<string, StaffMember>();
    staff.members.forEach((s) => { const k = lc(s.email); if (k) byEmail.set(k, s); });
    const claimed = new Set<string>();
    const rows: Person[] = members.map((m) => {
      const k = lc(m.email);
      const s = k ? byEmail.get(k) : undefined;
      if (s) claimed.add(k);
      return personFromMember(m, s, locName);
    });
    staff.members.forEach((s) => {
      const k = lc(s.email);
      if (k && claimed.has(k)) return;
      rows.push(personFromStaff(s, locName));
    });
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [members, staff.members, locName]);

  return {
    people,
    memberOf: (p) => members.find((m) => m.id === p.memberId),
    staffOf: (p) => staff.members.find((s) => s.id === p.staffId),
    locations: locs.locations.map((l) => ({ id: l.id, name: l.name })),
    loading: staff.loading || locs.loading,
    /* DEGRADE, don't fail closed — and never assert what a failed read can't
       tell us. Three reads back this screen and they fail independently. A
       screen-level error means there is genuinely nothing honest to show;
       otherwise the rows render and each missing half is disclosed inline.
       (2026-08-06 round 3: the member read failing silently was worse than
       the staff one — the table looked healthy while every row claimed "no
       sign-in" about people who very much have one.) */
    error: people.length === 0 && (membersError || staff.error || locs.error)
      ? (membersError ?? staff.error ?? locs.error ?? null)
      : null,
    membersUnavailable: !!membersError,
    bookabilityUnavailable: !!staff.error && members.length > 0,
    locationsUnavailable: !!locs.error,
    reload: () => { staff.reload(); locs.reload(); },
  };
}

/** Processes that have a role catalog to assign from, in rail order. */
export function assignableProcesses(): { id: ProcessId; label: string }[] {
  return PROCESSES.filter((p) => p.built).map((p) => ({ id: p.id, label: p.label }));
}
